"use client"

import { addToCart } from "@lib/data/cart"
import { useIntersection } from "@lib/hooks/use-in-view"
import { getProductPrice } from "@lib/util/get-product-price"
import { convertToLocale } from "@lib/util/money"
import { HttpTypes } from "@medusajs/types"
import { Button } from "@medusajs/ui"
import Divider from "@modules/common/components/divider"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import { isEqual } from "lodash"
import { useParams, usePathname, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import ProductPrice from "../product-price"
import MobileActions from "./mobile-actions"
import { useRouter } from "next/navigation"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  disabled?: boolean
}

const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) => {
  return variantOptions?.reduce((acc: Record<string, string>, varopt: any) => {
    acc[varopt.option_id] = varopt.value
    return acc
  }, {})
}

// Find the "Format" product option (case-insensitive) and resolve the
// variant whose value for that option matches the given target ("Box"/"Sample").
const findFormatVariant = (
  product: HttpTypes.StoreProduct,
  target: string
) => {
  const formatOption = (product.options || []).find(
    (o) => o.title?.trim().toLowerCase() === "format"
  )

  if (!formatOption) {
    return undefined
  }

  return product.variants?.find((v) =>
    (v.options || []).some(
      (vo: any) =>
        vo.option_id === formatOption.id &&
        vo.value?.trim().toLowerCase() === target.toLowerCase()
    )
  )
}

const isVariantInStock = (variant?: HttpTypes.StoreProductVariant) => {
  if (!variant) {
    return false
  }
  if (!variant.manage_inventory) {
    return true
  }
  if (variant.allow_backorder) {
    return true
  }
  if (variant.manage_inventory && (variant.inventory_quantity || 0) > 0) {
    return true
  }
  return false
}

export default function ProductActions({
  product,
  disabled,
}: ProductActionsProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [options, setOptions] = useState<Record<string, string | undefined>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [isAddingSample, setIsAddingSample] = useState(false)
  const [boxQuantity, setBoxQuantity] = useState(1)
  const countryCode = useParams().countryCode as string

  // Resolve the by-the-box and sample variants via the "Format" option.
  const boxVariant = useMemo(
    () => findFormatVariant(product, "Box"),
    [product]
  )
  const sampleVariant = useMemo(
    () => findFormatVariant(product, "Sample"),
    [product]
  )

  // A product is "tiered" only when BOTH Format variants resolve.
  const isTiered = !!boxVariant && !!sampleVariant

  // Tile metadata for coverage + per-sqft reference.
  const md = (product.metadata ?? {}) as Record<string, any>
  const sqftPerBox =
    md.sqftPerBox != null && Number.isFinite(Number(md.sqftPerBox))
      ? Number(md.sqftPerBox)
      : undefined
  const pricePerSqft =
    md.pricePerSqft != null && Number.isFinite(Number(md.pricePerSqft))
      ? Number(md.pricePerSqft)
      : undefined
  const samplePriceMeta =
    md.samplePrice != null && Number.isFinite(Number(md.samplePrice))
      ? Number(md.samplePrice)
      : undefined

  // If there is only 1 variant, preselect the options
  useEffect(() => {
    if (product.variants?.length === 1) {
      const variantOptions = optionsAsKeymap(product.variants[0].options)
      setOptions(variantOptions ?? {})
    }
  }, [product.variants])

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return
    }

    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  // update the options when a variant is selected
  const setOptionValue = (optionId: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      [optionId]: value,
    }))
  }

  //check if the selected options produce a valid variant
  const isValidVariant = useMemo(() => {
    return product.variants?.some((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const value = isValidVariant ? selectedVariant?.id : null

    if (params.get("v_id") === value) {
      return
    }

    if (value) {
      params.set("v_id", value)
    } else {
      params.delete("v_id")
    }

    router.replace(pathname + "?" + params.toString())
  }, [selectedVariant, isValidVariant])

  // check if the selected variant is in stock
  const inStock = useMemo(() => {
    return isVariantInStock(selectedVariant)
  }, [selectedVariant])

  const boxInStock = useMemo(
    () => isVariantInStock(boxVariant),
    [boxVariant]
  )

  const actionsRef = useRef<HTMLDivElement>(null)

  const inView = useIntersection(actionsRef, "0px")

  // add the selected variant to the cart (generic / non-tiered path)
  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return null

    setIsAdding(true)

    await addToCart({
      variantId: selectedVariant.id,
      quantity: 1,
      countryCode,
    })

    setIsAdding(false)
  }

  // add the BOX variant x quantity to the cart (tiered path)
  const handleAddBoxToCart = async () => {
    if (!boxVariant?.id) return

    setIsAdding(true)

    await addToCart({
      variantId: boxVariant.id,
      quantity: boxQuantity,
      countryCode,
    })

    setIsAdding(false)
  }

  // add the SAMPLE variant x 1 to the cart (tiered path)
  const handleAddSampleToCart = async () => {
    if (!sampleVariant?.id) return

    setIsAddingSample(true)

    await addToCart({
      variantId: sampleVariant.id,
      quantity: 1,
      countryCode,
    })

    setIsAddingSample(false)
  }

  // ---- TIERED Box/Sample buy box ----
  if (isTiered) {
    const boxPrices = getProductPrice({ product, variantId: boxVariant!.id })
    const boxPrice = boxPrices.variantPrice
    const samplePrices = getProductPrice({
      product,
      variantId: sampleVariant!.id,
    })
    const samplePrice = samplePrices.variantPrice

    const currency =
      boxPrice?.currency_code || samplePrice?.currency_code || "usd"

    // Per-box reference price: prefer the real calculated Box variant price,
    // fall back to pricePerSqft x sqftPerBox from metadata.
    const perBoxDisplay =
      boxPrice?.calculated_price ||
      (pricePerSqft != null && sqftPerBox != null
        ? convertToLocale({ amount: pricePerSqft * sqftPerBox, currency_code: currency })
        : null)

    const sampleDisplay =
      samplePrice?.calculated_price ||
      (samplePriceMeta != null
        ? convertToLocale({ amount: samplePriceMeta, currency_code: currency })
        : null)

    const coverageSqft =
      sqftPerBox != null ? boxQuantity * sqftPerBox : undefined

    return (
      <>
        <div className="flex flex-col gap-y-4" ref={actionsRef}>
          <ProductPrice product={product} variant={boxVariant} />

          {/* BOX buy box */}
          <div className="flex flex-col gap-y-3">
            <div className="flex flex-col gap-y-1">
              <span className="text-sm text-muted">Quantity (boxes)</span>
              <div className="flex items-center gap-x-3">
                <div className="flex items-center border border-line rounded-md bg-surface">
                  <button
                    type="button"
                    onClick={() =>
                      setBoxQuantity((q) => Math.max(1, q - 1))
                    }
                    disabled={!!disabled || isAdding || boxQuantity <= 1}
                    className="w-10 h-10 text-ink text-lg disabled:text-muted disabled:opacity-50"
                    aria-label="Decrease box quantity"
                    data-testid="box-quantity-decrement"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={boxQuantity}
                    onChange={(e) => {
                      const n = Math.floor(Number(e.target.value))
                      setBoxQuantity(Number.isFinite(n) && n >= 1 ? n : 1)
                    }}
                    className="w-12 h-10 text-center bg-surface text-ink border-x border-line focus:outline-none"
                    aria-label="Box quantity"
                    data-testid="box-quantity-input"
                  />
                  <button
                    type="button"
                    onClick={() => setBoxQuantity((q) => q + 1)}
                    disabled={!!disabled || isAdding}
                    className="w-10 h-10 text-ink text-lg disabled:text-muted disabled:opacity-50"
                    aria-label="Increase box quantity"
                    data-testid="box-quantity-increment"
                  >
                    +
                  </button>
                </div>
                {perBoxDisplay && (
                  <span className="text-sm text-ink">
                    {perBoxDisplay}
                    <span className="text-muted"> / box</span>
                    {pricePerSqft != null && (
                      <span className="text-muted">
                        {" "}
                        ({convertToLocale({ amount: pricePerSqft, currency_code: currency })}{" "}
                        / sq ft)
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>

            {coverageSqft != null && (
              <p className="text-sm text-ink" data-testid="coverage-line">
                Coverage: {boxQuantity} {boxQuantity === 1 ? "box" : "boxes"} ={" "}
                {coverageSqft.toFixed(0)} sq ft
              </p>
            )}

            <Button
              onClick={handleAddBoxToCart}
              disabled={!boxInStock || !!disabled || isAdding}
              variant="primary"
              className="w-full h-10 bg-clay hover:bg-clay-deep border-clay"
              isLoading={isAdding}
              data-testid="add-product-button"
            >
              {!boxInStock ? "Made to order" : "Add to Cart"}
            </Button>
          </div>

          {/* SAMPLE card */}
          <div className="flex flex-col gap-y-2 rounded-md border border-sage bg-card p-4">
            <p className="text-ink text-sm">
              <span className="font-medium">Order a sample</span>
              {sampleDisplay ? ` — ${sampleDisplay}` : ""} · ships in 1-2
              business days from Austin, TX
            </p>
            <Button
              onClick={handleAddSampleToCart}
              disabled={!!disabled || isAddingSample}
              variant="secondary"
              className="w-full h-10 border-sage text-sage hover:bg-bone"
              isLoading={isAddingSample}
              data-testid="add-sample-button"
            >
              Add Sample
            </Button>
            <p className="text-xs text-muted">Samples are final sale.</p>
          </div>

          <MobileActions
            product={product}
            variant={boxVariant}
            options={options}
            updateOptions={setOptionValue}
            inStock={boxInStock}
            handleAddToCart={handleAddBoxToCart}
            isAdding={isAdding}
            show={!inView}
            optionsDisabled={!!disabled || isAdding}
            sampleVariant={sampleVariant}
            handleAddSample={handleAddSampleToCart}
            isAddingSample={isAddingSample}
          />
        </div>
      </>
    )
  }

  // ---- GENERIC (non-tiered) fallback: original behavior ----
  return (
    <>
      <div className="flex flex-col gap-y-2" ref={actionsRef}>
        <div>
          {(product.variants?.length ?? 0) > 1 && (
            <div className="flex flex-col gap-y-4">
              {(product.options || []).map((option) => {
                return (
                  <div key={option.id}>
                    <OptionSelect
                      option={option}
                      current={options[option.id]}
                      updateOption={setOptionValue}
                      title={option.title ?? ""}
                      data-testid="product-options"
                      disabled={!!disabled || isAdding}
                    />
                  </div>
                )
              })}
              <Divider />
            </div>
          )}
        </div>

        <ProductPrice product={product} variant={selectedVariant} />

        <Button
          onClick={handleAddToCart}
          disabled={
            !inStock ||
            !selectedVariant ||
            !!disabled ||
            isAdding ||
            !isValidVariant
          }
          variant="primary"
          className="w-full h-10"
          isLoading={isAdding}
          data-testid="add-product-button"
        >
          {!selectedVariant && !options
            ? "Select variant"
            : !inStock || !isValidVariant
            ? "Out of stock"
            : "Add to cart"}
        </Button>
        <MobileActions
          product={product}
          variant={selectedVariant}
          options={options}
          updateOptions={setOptionValue}
          inStock={inStock}
          handleAddToCart={handleAddToCart}
          isAdding={isAdding}
          show={!inView}
          optionsDisabled={!!disabled || isAdding}
        />
      </div>
    </>
  )
}
